import {
  closestCenter,
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical, MessageSquare, Phone, Send } from "lucide-react";
import { useTranslation } from "react-i18next";
import { SettingCard } from "@/components/settings/setting-card";
import { SettingField } from "@/components/settings/setting-field";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";

export interface PhoneProvider {
  id: string;
  type: string;
  enabled: boolean;
  order: number;
  draggable: boolean;
  name: string;
  priority: string;
}

export interface GeneralChannelSettings {
  emailEnabled: boolean;
  phoneProvidersEnabled: boolean; // Master toggle for phone providers
  phoneProviders: PhoneProvider[];
}
export type ChannelToggleChange =
  | { kind: "email"; enabled: boolean }
  | { kind: "phoneProviders"; enabled: boolean }
  | { kind: "provider"; providerId: string; enabled: boolean };

/**
 * Turning the master switch off disables every provider. Turning it on keeps
 * the enabled providers, or enables all of them when none is enabled, because
 * the master switch state is derived from the providers.
 */
export function setPhoneProvidersEnabled(
  settings: GeneralChannelSettings,
  enabled: boolean
): GeneralChannelSettings {
  const noneEnabled = !settings.phoneProviders.some((provider) => provider.enabled);
  return {
    ...settings,
    phoneProvidersEnabled: enabled,
    phoneProviders: settings.phoneProviders.map((provider) => ({
      ...provider,
      enabled: enabled && (provider.enabled || noneEnabled),
    })),
  };
}

interface GeneralChannelSettingsCardProps {
  title: string;
  description: string;
  settings: GeneralChannelSettings;
  onChange: (settings: GeneralChannelSettings) => void;
  disabled?: boolean;
  onToggle: (change: ChannelToggleChange) => void;
  togglesSaving: boolean;
}

export function GeneralChannelSettingsCard({
  title,
  description,
  settings,
  onChange,
  disabled = false,
  onToggle,
  togglesSaving,
}: GeneralChannelSettingsCardProps) {
  const { t } = useTranslation("settings");

  const handleEmailToggle = (enabled: boolean) => {
    onChange({
      ...settings,
      emailEnabled: enabled,
    });
    onToggle({ kind: "email", enabled });
  };

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handlePhoneProvidersToggle = (enabled: boolean) => {
    onChange(setPhoneProvidersEnabled(settings, enabled));
    onToggle({ kind: "phoneProviders", enabled });
  };

  const handlePhoneProviderToggle = (providerId: string, enabled: boolean) => {
    onChange({
      ...settings,
      phoneProviders: settings.phoneProviders.map((provider) =>
        provider.id === providerId ? { ...provider, enabled } : provider
      ),
    });
    onToggle({ kind: "provider", providerId, enabled });
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (active.id !== over?.id) {
      const oldIndex = settings.phoneProviders.findIndex((item) => item.id === active.id);
      const newIndex = settings.phoneProviders.findIndex((item) => item.id === over?.id);

      const newPhoneProviders = arrayMove(settings.phoneProviders, oldIndex, newIndex).map(
        (provider, index) => ({
          ...provider,
          order: index + 1,
        })
      );

      onChange({
        ...settings,
        phoneProviders: newPhoneProviders,
      });
    }
  };

  const getProviderIcon = (type: string) => {
    switch (type) {
      case "whatsapp":
        return <MessageSquare className="h-4 w-4 text-green-600" />;
      case "telegram":
        return <Send className="h-4 w-4 text-blue-500" />;
      case "sms":
        return <Phone className="h-4 w-4 text-orange-500" />;
      default:
        return <Phone className="h-4 w-4" />;
    }
  };

  // Sortable provider item component
  function SortableProviderItem({ provider }: { provider: PhoneProvider }) {
    const { attributes, listeners, setNodeRef, transform, transition } = useSortable({
      id: provider.id,
    });

    const style = {
      transform: CSS.Transform.toString(transform),
      transition,
    };

    return (
      <div
        ref={setNodeRef}
        style={style}
        className="flex items-center justify-between p-2 bg-background rounded border"
      >
        <div className="flex items-center space-x-3">
          {provider.draggable && (
            <div {...attributes} {...listeners} className="cursor-grab active:cursor-grabbing">
              <GripVertical className="h-4 w-4 text-muted-foreground" />
            </div>
          )}
          {getProviderIcon(provider.type)}
          <div className="flex flex-col">
            <Label className="text-sm">{provider.name}</Label>
            <span className="text-xs text-muted-foreground">{provider.priority}</span>
          </div>
        </div>
        <Switch
          checked={provider.enabled}
          onCheckedChange={(enabled) => handlePhoneProviderToggle(provider.id, enabled)}
          disabled={disabled || togglesSaving}
        />
      </div>
    );
  }

  return (
    <SettingCard title={title} helpText={description}>
      <div className="space-y-6">
        {/* Email Settings */}
        <SettingField
          label={t("notifications.channels.email", { defaultValue: "Email" })}
          helpText={t("notifications.emailDescription", { defaultValue: "Send via email" })}
        >
          <Switch
            id="email-toggle"
            checked={settings.emailEnabled}
            onCheckedChange={handleEmailToggle}
            disabled={disabled || togglesSaving}
          />
        </SettingField>

        {/* Phone Providers Section */}
        <div className="space-y-4">
          {/* Master Phone Providers Toggle */}
          <SettingField
            label={t("notifications.phoneProviders", { defaultValue: "Phone Providers" })}
            helpText={t("notifications.phoneProvidersDescription", {
              defaultValue: "Enable phone-based notifications",
            })}
            helpDetails={{
              title: t("notifications.channelOrderTitle", { defaultValue: "Delivery order" }),
              description: t("notifications.channelOrder", {
                defaultValue:
                  "Email sent separately. Phone chain: WhatsApp → Telegram → SMS (fallback)",
              }),
              benefits: [
                t("notifications.retriesFallback", {
                  defaultValue: "Automatic retries and fallback across providers",
                }),
              ],
            }}
          >
            <Switch
              id="phone-providers-toggle"
              checked={settings.phoneProvidersEnabled}
              onCheckedChange={handlePhoneProvidersToggle}
              disabled={disabled || togglesSaving}
            />
          </SettingField>

          {/* Individual Phone Providers - Only show when phone providers are enabled */}
          {settings.phoneProvidersEnabled && (
            <div className="pl-4 md:pl-8 border-l-2 border-muted">
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <SortableContext
                  items={settings.phoneProviders.map((p) => p.id)}
                  strategy={verticalListSortingStrategy}
                >
                  <div className="space-y-2">
                    {settings.phoneProviders
                      .sort((a, b) => a.order - b.order)
                      .map((provider) => (
                        <SortableProviderItem key={provider.id} provider={provider} />
                      ))}
                  </div>
                </SortableContext>
              </DndContext>
            </div>
          )}

          {/* Show message when phone providers are disabled */}
          {!settings.phoneProvidersEnabled && (
            <div className="pl-4 md:pl-8 text-sm text-muted-foreground">
              {t("notifications.phoneProvidersDisabled", {
                defaultValue: "Enable phone providers to configure individual services",
              })}
            </div>
          )}
        </div>
      </div>
    </SettingCard>
  );
}
